import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Plus, Table2, BarChart3 } from "lucide-react";
import {
  FIELD_CATALOG,
  AGG_FNS,
  getDimensionFields,
  getMeasureFields,
  getAllFields,
  getFieldByKey,
  type AggFn,
  type FieldDef,
  type FieldGroup,
  type FlatEmployeeRow,
} from "@/lib/analytics/fieldCatalog";
import type { PivotConfig, PivotValueSpec, PivotFilter, FilterOp } from "@/lib/analytics/pivot";
import type { ReportTile, TileChartType } from "./types";

const FIELD_GROUPS: FieldGroup[] = ["Identity", "Org", "Personal", "Employment", "Salary", "Leave", "Compliance", "Counts"];

export function PivotConfigPanel({
  tile,
  onChange,
}: {
  tile: ReportTile;
  onChange: (patch: Partial<ReportTile>) => void;
}) {
  const { t } = useTranslation();
  const { config } = tile;

  const labelFor = (key: keyof FlatEmployeeRow): string => {
    const f = getFieldByKey(key as string);
    return f ? t(f.labelKey) : String(key);
  };
  const aggLabel = (agg: AggFn) => t(`analytics.pivot.agg.${agg}`);

  const patchConfig = (patch: Partial<PivotConfig>) => onChange({ config: { ...config, ...patch } });

  function groupedOptions(fields: FieldDef[]) {
    return FIELD_GROUPS.map((g) => {
      const items = fields.filter((f) => f.group === g);
      if (!items.length) return null;
      return (
        <SelectGroup key={g}>
          <SelectLabel>{t(`analytics.pivot.groups.${g}`)}</SelectLabel>
          {items.map((f) => (
            <SelectItem key={f.key} value={f.key as string}>
              {t(f.labelKey)}
            </SelectItem>
          ))}
        </SelectGroup>
      );
    });
  }

  const availableRowFields = getDimensionFields().filter((f) => !config.rowFields.includes(f.key));
  const measureFields = getMeasureFields();

  const addRowField = (k: string) => patchConfig({ rowFields: [...config.rowFields, k as keyof FlatEmployeeRow] });
  const removeRowField = (k: keyof FlatEmployeeRow) => patchConfig({ rowFields: config.rowFields.filter((x) => x !== k) });
  const addValue = () => {
    const first = measureFields[0];
    if (first) patchConfig({ values: [...config.values, { field: first.key, agg: "sum" }] });
  };
  const updateValue = (i: number, p: Partial<PivotValueSpec>) =>
    patchConfig({ values: config.values.map((s, idx) => (idx === i ? { ...s, ...p } : s)) });
  const removeValue = (i: number) => patchConfig({ values: config.values.filter((_, idx) => idx !== i) });
  const addFilter = () => patchConfig({ filters: [...config.filters, { field: FIELD_CATALOG[0].key, op: "eq", value: "" }] });
  const updateFilter = (i: number, p: Partial<PivotFilter>) =>
    patchConfig({ filters: config.filters.map((s, idx) => (idx === i ? { ...s, ...p } : s)) });
  const removeFilter = (i: number) => patchConfig({ filters: config.filters.filter((_, idx) => idx !== i) });
  const toggleFlatColumn = (k: string) => {
    const cur = config.flatColumns ?? [];
    const key = k as keyof FlatEmployeeRow;
    patchConfig({ flatColumns: cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key] });
  };

  return (
    <div className="space-y-4">
      {/* Tile title */}
      <div className="space-y-1.5">
        <Label>{t("analytics.pivot.tileTitle")}</Label>
        <Input value={tile.title} onChange={(e) => onChange({ title: e.target.value })} className="h-8" />
      </div>

      {/* Mode */}
      <div className="space-y-1.5">
        <Label>{t("analytics.pivot.mode")}</Label>
        <div className="inline-flex rounded-md border p-0.5 w-full">
          <Button type="button" size="sm" className="flex-1" variant={config.mode === "pivot" ? "default" : "ghost"} onClick={() => patchConfig({ mode: "pivot" })}>
            {t("analytics.pivot.modePivot")}
          </Button>
          <Button type="button" size="sm" className="flex-1" variant={config.mode === "flat" ? "default" : "ghost"} onClick={() => patchConfig({ mode: "flat" })}>
            {t("analytics.pivot.modeFlat")}
          </Button>
        </div>
      </div>

      {config.mode === "pivot" ? (
        <>
          {/* Rows */}
          <div className="space-y-1.5">
            <Label>{t("analytics.pivot.rows")}</Label>
            <div className="flex flex-wrap items-center gap-1.5">
              {config.rowFields.map((k) => (
                <Badge key={k} variant="secondary" className="gap-1">
                  {labelFor(k)}
                  <button type="button" onClick={() => removeRowField(k)} className="hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            {availableRowFields.length > 0 && (
              <Select value="" onValueChange={addRowField}>
                <SelectTrigger className="h-8">
                  <SelectValue placeholder={t("analytics.pivot.addRowField")} />
                </SelectTrigger>
                <SelectContent>{groupedOptions(availableRowFields)}</SelectContent>
              </Select>
            )}
          </div>

          {/* Columns */}
          <div className="space-y-1.5">
            <Label>{t("analytics.pivot.columns")}</Label>
            <Select
              value={config.colField || "__none__"}
              onValueChange={(v) => patchConfig({ colField: v === "__none__" ? undefined : (v as keyof FlatEmployeeRow) })}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("analytics.pivot.none")}</SelectItem>
                {groupedOptions(getDimensionFields())}
              </SelectContent>
            </Select>
          </div>

          {/* Values */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>{t("analytics.pivot.values")}</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addValue}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t("analytics.pivot.addValue")}
              </Button>
            </div>
            {config.values.length === 0 && <p className="text-xs text-muted-foreground">{t("analytics.pivot.headcountHint")}</p>}
            <div className="space-y-2">
              {config.values.map((v, i) => {
                const field = getFieldByKey(v.field as string);
                const aggs = field?.allowedAggs ?? AGG_FNS;
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    <Select value={v.agg} onValueChange={(a) => updateValue(i, { agg: a as AggFn })}>
                      <SelectTrigger className="h-8 w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {aggs.map((a) => (
                          <SelectItem key={a} value={a}>
                            {aggLabel(a)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={v.field as string} onValueChange={(f) => updateValue(i, { field: f as keyof FlatEmployeeRow })}>
                      <SelectTrigger className="h-8 flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{groupedOptions(measureFields)}</SelectContent>
                    </Select>
                    <button type="button" onClick={() => removeValue(i)} className="text-muted-foreground hover:text-destructive">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Output */}
          <div className="space-y-1.5">
            <Label>{t("analytics.pivot.output")}</Label>
            <div className="inline-flex rounded-md border p-0.5 w-full">
              <Button type="button" size="sm" className="flex-1" variant={tile.output === "table" ? "default" : "ghost"} onClick={() => onChange({ output: "table" })}>
                <Table2 className="h-3.5 w-3.5 mr-1" />
                {t("analytics.pivot.outputTable")}
              </Button>
              <Button type="button" size="sm" className="flex-1" variant={tile.output === "chart" ? "default" : "ghost"} onClick={() => onChange({ output: "chart" })}>
                <BarChart3 className="h-3.5 w-3.5 mr-1" />
                {t("analytics.pivot.outputChart")}
              </Button>
            </div>
            {tile.output === "chart" && (
              <Select value={tile.chartType} onValueChange={(v) => onChange({ chartType: v as TileChartType })}>
                <SelectTrigger className="h-8 mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">{t("analytics.pivot.chart.bar")}</SelectItem>
                  <SelectItem value="line">{t("analytics.pivot.chart.line")}</SelectItem>
                  <SelectItem value="pie">{t("analytics.pivot.chart.pie")}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </>
      ) : (
        /* Flat column picker */
        <div className="space-y-1.5">
          <Label>{t("analytics.pivot.flatColumns")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {getAllFields().map((f) => {
              const on = (config.flatColumns ?? []).includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => toggleFlatColumn(f.key as string)}
                  className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                    on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {t(f.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label>{t("analytics.pivot.filters")}</Label>
          <Button type="button" variant="ghost" size="sm" onClick={addFilter}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            {t("analytics.pivot.addFilter")}
          </Button>
        </div>
        {config.filters.map((f, i) => (
          <div key={i} className="space-y-1.5 rounded-md border p-2">
            <div className="flex items-center gap-1.5">
              <Select value={f.field as string} onValueChange={(v) => updateFilter(i, { field: v as keyof FlatEmployeeRow })}>
                <SelectTrigger className="h-8 flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{groupedOptions(FIELD_CATALOG)}</SelectContent>
              </Select>
              <button type="button" onClick={() => removeFilter(i)} className="text-muted-foreground hover:text-destructive">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <Select value={f.op} onValueChange={(v) => updateFilter(i, { op: v as FilterOp })}>
                <SelectTrigger className="h-8 w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="eq">{t("analytics.pivot.op.eq")}</SelectItem>
                  <SelectItem value="neq">{t("analytics.pivot.op.neq")}</SelectItem>
                  <SelectItem value="gte">{t("analytics.pivot.op.gte")}</SelectItem>
                  <SelectItem value="lte">{t("analytics.pivot.op.lte")}</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={String(f.value ?? "")}
                onChange={(e) => updateFilter(i, { value: e.target.value })}
                placeholder={t("analytics.pivot.filterValue")}
                className="h-8 flex-1"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
